import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@jq4race/api-client';
import { Trans, translateApiError, useTranslation } from '@jq4race/i18n';
import { type LoginRequest, LoginRequestSchema } from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import logoPrimary from '@jq4race/ui/assets/logos/logo-primary.png';
import patternDecoFan from '@jq4race/ui/assets/patterns/pattern-cream-deco-fan.png';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { requestPersistentStorage } from '../../../../app/persist-storage';
import { NonOperatorLoginError } from '../../application/use-cases/login.use-case';
import { useAuthServices } from '../auth-services-context';
import { useAuthState } from '../auth-state-context';

export function LoginPage() {
  const { t } = useTranslation('auth');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const auth = useAuthServices();
  const { setSession, sessionExpired, sessionExpiredReason, clearSessionExpired } = useAuthState();
  const navigate = useNavigate();

  // Captured once at mount so the notice this render is showing doesn't
  // vanish the instant clearSessionExpired() flips the store's flag below —
  // a voluntary sign-out never sets sessionExpired, so it renders nothing.
  const [showSessionExpiredNotice] = useState(sessionExpired);
  // QA-I11: the refresh was rejected because the organization is suspended.
  const [noticeKey] = useState(
    sessionExpiredReason === 'organization_suspended' ? 'orgSuspended' : 'sessionExpired',
  );

  // Runs once on mount: this notice should not stay stuck for a later
  // visit, but re-running it on every sessionExpired change would just
  // call clearSessionExpired repeatedly for no benefit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only effect, see comment above
  useEffect(() => {
    if (sessionExpired) clearSessionExpired();
  }, []);

  const form = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });

  async function onSubmit(values: LoginRequest) {
    setError(null);
    try {
      const result = await auth.login(values);
      setSession(result.session);
      // Fire-and-forget: never block navigation on the browser's persistence
      // prompt/decision. See persist-storage.ts (ADR-017 / design D17).
      void requestPersistentStorage();
      await navigate({ to: '/queue' });
    } catch (err) {
      if (err instanceof NonOperatorLoginError) {
        setError(t('errors.nonOperator'));
        return;
      }
      if (err instanceof ApiError) {
        setError(translateApiError(err, t));
        return;
      }
      setError(t('errors.generic'));
    }
  }

  const { errors, isSubmitting } = form.formState;

  return (
    <main className="relative min-h-screen overflow-hidden bg-jq-paper text-foreground">
      {/* Cream deco-fan texture as the full backdrop */}
      <img
        src={patternDecoFan}
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.13] [mask-image:radial-gradient(ellipse_at_center,black_65%,transparent_100%)]"
      />

      {/* Top hairline accent — fixed at top of viewport */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-5 z-10 flex items-center justify-between px-6 text-[10px] uppercase tracking-widest text-jq-gold-bright sm:top-6 sm:px-8 md:top-8 md:px-10"
      >
        <span className="flex items-center gap-2">
          <span className="h-px w-6 bg-jq-gold/60" />
          <span>{t('eyebrow')}</span>
        </span>
        <span className="flex items-center gap-2">
          <span>{t('privacyBadge')}</span>
          <span className="h-1 w-1 rounded-full bg-jq-gold" />
        </span>
      </div>

      {/* Content container */}
      <div className="relative flex min-h-screen flex-col items-center justify-center px-6 pb-10 pt-16 sm:px-8 md:px-10 md:pb-10 md:pt-20">
        {/* Form card — centered both on mobile and desktop, single-column mobile-first */}
        <div className="w-full max-w-md">
          <header className="flex justify-center">
            <img
              src={logoPrimary}
              alt={t('logoAlt')}
              className="h-28 w-auto drop-shadow-lg sm:h-36 md:h-32"
            />
          </header>

          <h1 className="mt-6 text-center font-editorial text-[34px] font-medium leading-[1.1] tracking-tight text-jq-navy sm:text-[42px] md:text-[38px]">
            {t('heading')}
          </h1>
          <p className="mt-2 text-center text-[14px] leading-relaxed text-jq-graphite">
            {t('subheading')}
          </p>

          <form className="mt-7 grid gap-5" onSubmit={form.handleSubmit(onSubmit)} noValidate>
            {showSessionExpiredNotice ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-jq-gold/60 bg-jq-gold/10 px-3 py-2 text-[13px] font-medium text-jq-navy"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-jq-gold-bright" />
                <span>
                  <strong className="font-semibold">{t(`${noticeKey}.title`)}</strong>{' '}
                  {t(`${noticeKey}.body`)}
                </span>
              </div>
            ) : null}

            {error ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/60 bg-destructive/10 px-3 py-2 text-[13px] font-medium text-foreground"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="grid gap-2">
              <Label htmlFor="email">{t('form.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="off"
                placeholder={t('form.emailPlaceholder')}
                aria-invalid={!!errors.email}
                className="h-12"
                {...form.register('email')}
              />
              {errors.email ? (
                <p className="text-[13px] text-destructive">{errors.email.message}</p>
              ) : null}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password">{t('form.passwordLabel')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  className="h-12 pr-11"
                  {...form.register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-jq-graphite transition hover:text-jq-navy"
                  aria-label={showPassword ? t('form.hidePassword') : t('form.showPassword')}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {errors.password ? (
                <p className="text-[13px] text-destructive">{errors.password.message}</p>
              ) : null}
            </div>

            <Button type="submit" disabled={isSubmitting} className="mt-2 h-12 w-full">
              {isSubmitting ? t('form.submitting') : t('form.submit')}
            </Button>
          </form>

          <p className="mt-8 text-center text-[12px] text-jq-graphite">
            <Trans
              i18nKey="auth:help.prompt"
              components={{ styled: <span className="font-medium text-jq-navy" /> }}
            />
          </p>
        </div>
      </div>

      {/* Page footer — pinned to viewport bottom on all sizes */}
      <footer className="pointer-events-none absolute inset-x-0 bottom-4 z-10 text-center text-[11px] uppercase tracking-widest text-jq-stone sm:bottom-6">
        {t('footer', { year: new Date().getFullYear() })}
      </footer>
    </main>
  );
}
